import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useLocation } from "react-router-dom";
import { resetPasswordSchema } from "../../schemas/resetPasswordSchema";
import { useResetPassword } from "../../custom-hooks/queries";
import { toast } from "../../components/common/Toast";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const { mutate: resetPassword, isPending } = useResetPassword();

  // 👉 Show/Hide State
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
  });

  const onSubmit = (data) => {
    if (!email) {
      toast.error("Email not found. Please start from forgot password page.");
      navigate("/forgot-password");
      return;
    }

    resetPassword(
      {
        email,
        newPassword: data.password,
        confirmPassword: data.confirmPassword,
      },
      {
        onSuccess: () => {
          toast.success("Password reset successfully!");
          navigate("/signin");
        },
        onError: (error) => {
          toast.error(
            error.response?.data?.message ||
              "Failed to reset password. Please try again."
          );
        },
      }
    );
  };

  return (
    <div className="flex items-center justify-center px-4 py-6 sm:py-8">
      <div className="w-full max-w-[500px] border border-gray-200 shadow-sm rounded-2xl p-6 sm:p-10 bg-white">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-2">
          Reset Password
        </h1>
        <p className="text-sm sm:text-base text-gray-600 text-center mb-4">
          Enter your new password below.
        </p>

        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="w-[165px] h-[5px] bg-[#16BBD8] rounded"></div>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* NEW PASSWORD */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-800 mb-1">
              New Password
            </label>

            <input
              type={showPass ? "text" : "password"}
              {...register("password")}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1FADC6] placeholder-gray-600 ${
                errors.password ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter new password"
            />

            <button
              type="button"
              onClick={() => setShowPass(!showPass)}
              className="absolute right-3 top-12 transform -translate-y-1/2 text-gray-600"
            >
              {showPass ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
            </button>

            {errors.password && (
              <p className="mt-1 text-sm text-red-600">
                {errors.password.message}
              </p>
            )}
          </div>

          {/* CONFIRM PASSWORD */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-800 mb-1">
              Confirm Password
            </label>

            <input
              type={showConfirm ? "text" : "password"}
              {...register("confirmPassword")}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#1FADC6] placeholder-gray-600 ${
                errors.confirmPassword ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Confirm new password"
            />

            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-12 transform -translate-y-1/2 text-gray-600"
            >
              {showConfirm ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
            </button>

            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* SUBMIT BUTTON */}
          <button
            type="submit"
            disabled={isPending}
            className="w-full bg-[#00C2C7] hover:bg-[#1FADC6] text-white font-medium py-3 px-4 rounded-lg transition-colors focus:ring-2 focus:ring-[#1FADC6] disabled:opacity-50"
          >
            {isPending ? "Resetting..." : "Reset Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
